/**
 * Other nodes, in the Command rail's COT tab: the aircraft they fly and the
 * signals they detected. Read-only, for admins and observers.
 *
 * Nothing here selects, follows or commands. A remote aircraft is listed by
 * its `node:drone` key with REMOTE beside it, and a node that has gone quiet
 * says how long ago it last reported instead of looking current.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  getSightings,
  getSignals,
  type IsrSighting,
  type IsrSignal,
  type RemoteAircraft,
  type SignalType,
} from '@/api/federation';
import { SIGNAL_COLORS } from '@/map/signalColors';
import { useCanSeeRemote, useFederationStore } from '@/stores/federationStore';

/** The catalog changes when an edge reports; ten seconds is a few reports. */
const CATALOG_POLL_MS = 10_000;
const MAX_ROWS = 200;

export const SIGNAL_LABELS: Record<SignalType, string> = {
  wifi: 'Wi-Fi',
  bluetooth: 'Bluetooth',
  adsb: 'ADS-B',
  ais: 'AIS',
  rfid: 'RFID',
  cellular: 'Cellular',
  lora: 'LoRa',
  zigbee: 'Zigbee',
  gnss: 'GNSS',
  radar: 'Radar',
  rf: 'RF',
  unknown: 'Unknown',
};

export function ago(seconds: number): string {
  const s = Math.max(0, seconds);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

/** What to call a signal in a list: the name it broadcasts, else its own id. */
export function signalName(signal: IsrSignal): string {
  const d = signal.details;
  const named = d.ssid ?? d.name ?? d.callsign ?? d.vesselName ?? d.icao ?? d.mmsi ?? d.tagId;
  return named === undefined || named === '' ? signal.signalId : String(named);
}

export function detailRows(signal: IsrSignal): Array<[string, string]> {
  const rows: Array<[string, string | number | null | undefined]> = [
    ['Node', signal.nodeId],
    ['Seen by', signal.detectedBy],
    ['Sightings', signal.sightings],
    ['Strength', signal.strengthDb === null ? null : `${signal.strengthDb.toFixed(1)} dB`],
    ['Frequency', signal.frequencyMhz === null ? null : `${signal.frequencyMhz} MHz`],
    ['Confidence', signal.confidence === null ? null : `${Math.round(signal.confidence * 100)}%`],
    [
      'Last position',
      signal.lat === null || signal.lon === null
        ? null
        : `${signal.lat.toFixed(5)}, ${signal.lon.toFixed(5)}`,
    ],
    ['First seen', new Date(signal.firstSeen * 1000).toLocaleString()],
    ...Object.entries(signal.details).map(([k, v]): [string, string | number] => [k, v]),
  ];
  return rows
    .filter((row): row is [string, string | number] => row[1] !== null && row[1] !== undefined)
    .map(([k, v]) => [k, String(v)]);
}

function RemoteAircraftList({ aircraft }: { aircraft: RemoteAircraft[] }): ReactNode {
  if (aircraft.length === 0) {
    return (
      <p className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground)]">
        No other node is reporting aircraft. An edge pushes here when it runs with
        FEDERATION_CLOUD_URL and FEDERATION_PUSH_TOKEN, and this server holds its
        FEDERATION_TOKEN.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {aircraft.map((a) => {
        const stale = a.freshness === 'stale';
        return (
          <div
            key={a.key}
            className="flex items-center gap-2 text-[10.5px]"
            style={{ opacity: stale ? 0.6 : 1 }}
          >
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2"
              style={{ borderColor: stale ? '#71717a' : '#c084fc' }}
            />
            <span className="min-w-0 truncate font-mono" title={a.key}>{a.key}</span>
            <span className="label-mono shrink-0 text-[8px]" style={{ color: '#c084fc' }}>
              REMOTE
            </span>
            <span className="label-mono ml-auto shrink-0 text-[8.5px] text-[color:var(--muted-foreground-3)]">
              {stale
                ? `silent ${ago(a.ageS)}`
                : [
                    a.mode,
                    a.altRelative === null ? null : `${Math.round(a.altRelative)} m`,
                    a.batteryPercent === null ? null : `${Math.round(a.batteryPercent)}%`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SignalDetail({ signal }: { signal: IsrSignal }): ReactNode {
  const [sightings, setSightings] = useState<IsrSighting[] | null>(null);

  useEffect(() => {
    let live = true;
    void getSightings(signal.nodeId, signal.signalId).then((result) => {
      if (live) setSightings(result.ok ? result.data.sightings : []);
    });
    return () => {
      live = false;
    };
  }, [signal.nodeId, signal.signalId]);

  return (
    <div className="mt-1 mb-1.5 rounded-[8px] px-2 py-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
      {detailRows(signal).map(([k, v]) => (
        <div key={k} className="flex gap-2 text-[9.5px]">
          <span className="w-[78px] shrink-0 text-[color:var(--muted-foreground-3)]">{k}</span>
          <span className="min-w-0 truncate font-mono" title={String(v)}>{v}</span>
        </div>
      ))}
      {signal.clockSkewed ? (
        <p className="mt-1 text-[9px]" style={{ color: 'var(--caution)' }}>
          The node's clock disagreed with ours, so the time shown is when it reached this server.
        </p>
      ) : null}
      <p className="mt-1 text-[9px] text-[color:var(--muted-foreground-3)]">
        {sightings === null
          ? 'Reading sightings…'
          : `${sightings.length} sampled sighting${sightings.length === 1 ? '' : 's'} kept (one a minute, seven days).`}
      </p>
    </div>
  );
}

export function IsrPanel(): ReactNode {
  const [signals, setSignals] = useState<IsrSignal[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<SignalType | null>(null);
  const [node, setNode] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = (): void => {
      void getSignals({ limit: 1000 }).then((result) => {
        if (!live) return;
        setLoaded(true);
        if (result.ok) {
          setSignals(result.data.signals);
          setError(null);
        } else {
          setError(result.error.message);
        }
      });
    };
    load();
    const timer = setInterval(load, CATALOG_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, []);

  const byType = useMemo(() => {
    const counts = new Map<SignalType, number>();
    for (const s of signals) counts.set(s.signalType, (counts.get(s.signalType) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [signals]);
  const nodes = useMemo(() => [...new Set(signals.map((s) => s.nodeId))].sort(), [signals]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return signals
      .filter(
        (s) =>
          (!type || s.signalType === type) &&
          (!node || s.nodeId === node) &&
          (!q || signalName(s).toLowerCase().includes(q) || s.key.toLowerCase().includes(q)),
      )
      .slice(0, MAX_ROWS);
  }, [signals, type, node, search]);
  const now = Date.now() / 1000;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">ISR · OTHER NODES</span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {signals.length} EMITTERS · {nodes.length} NODES
        </span>
      </div>
      {error ? (
        <p className="text-[10px]" style={{ color: 'var(--danger-foreground)' }}>
          {error}
        </p>
      ) : null}
      {loaded && signals.length === 0 && !error ? (
        <p className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground)]">
          No signals from other nodes yet. An edge sends the ADS-B its own receiver hears when it
          runs with FEDERATION_PUSH_ISR=1.
        </p>
      ) : null}
      {signals.length > 0 ? (
        <>
          <div className="flex flex-wrap gap-1">
            {byType.map(([t, n]) => {
              const active = type === t;
              const color = SIGNAL_COLORS[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(active ? null : t)}
                  aria-pressed={active}
                  className="rounded-full border px-2 py-0.5 label-mono text-[8.5px]"
                  style={{
                    borderColor: active ? color : 'var(--border-nested)',
                    color: active ? color : 'var(--muted-foreground)',
                    background: active ? `${color}22` : 'transparent',
                  }}
                >
                  {SIGNAL_LABELS[t]} {n}
                </button>
              );
            })}
          </div>
          <div className="flex gap-1.5">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or id"
              aria-label="Search signals"
              className="well min-w-0 flex-1 rounded-[8px] px-2 py-1 text-[10.5px]"
            />
            {nodes.length > 1 ? (
              <select
                value={node}
                onChange={(e) => setNode(e.target.value)}
                aria-label="Filter by node"
                className="well rounded-[8px] px-1.5 py-1 text-[10px]"
              >
                <option value="">All nodes</option>
                {nodes.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <div className="flex flex-col">
            {shown.map((s) => (
              <div key={s.key}>
                <button
                  type="button"
                  onClick={() => setOpen(open === s.key ? null : s.key)}
                  aria-expanded={open === s.key}
                  className="flex w-full items-center gap-2 py-0.5 text-left text-[10.5px]"
                >
                  <span
                    aria-hidden
                    className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
                    style={{ background: SIGNAL_COLORS[s.signalType] }}
                  />
                  <span className="min-w-0 truncate" title={signalName(s)}>{signalName(s)}</span>
                  <span className="label-mono ml-auto shrink-0 text-[8.5px] text-[color:var(--muted-foreground-3)]">
                    {s.nodeId} · {ago(now - s.lastSeen)}
                  </span>
                </button>
                {open === s.key ? <SignalDetail signal={s} /> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function RemoteNodesPanel(): ReactNode {
  const allowed = useCanSeeRemote();
  const aircraft = useFederationStore((state) => state.aircraft);
  if (!allowed) return null;
  return (
    <div className="flex flex-col gap-2.5 py-1">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">OTHER NODES · AIRCRAFT</span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {aircraft.length} REMOTE
        </span>
      </div>
      <RemoteAircraftList aircraft={aircraft} />
      <IsrPanel />
      <p className="text-[9px] leading-relaxed text-[color:var(--muted-foreground-3)]">
        Shown, never flown: another node's aircraft are commanded on that node. Their ids carry the
        node's name so they cannot be confused with ours.
      </p>
    </div>
  );
}

export default RemoteNodesPanel;
